import { NextPageContext } from "next";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { ParsedUrlQuery } from "querystring";
import { PathInfo } from "./interface/pathInfo.interface";
import {
  BRAND_GEO_MAP,
  GENDER_CATEGORIES,
  GENDER_TRANSLATIONS,
} from "./constants/utils.constant";

export const parsePathInfo = (pathname: string): PathInfo => {
  const pathParams = pathname.split("/").filter((val) => val !== "");

  return {
    pathParams,
    isGenderHomePage: pathParams.length === 1,
    isBrandPage:
      pathParams.length === 3 &&
      Object.values(BRAND_GEO_MAP).includes(pathParams[0]),
    genderParam: pathParams.length === 1 ? pathParams[0] : pathParams[2],
    brandParam: pathParams.length === 3 ? pathParams[1] : undefined,
  };
};

/**
 * Maps a locale string to a corresponding domain.
 * @param locale - The locale string to map.
 * @returns The corresponding domain string.
 */
export const localeToDomain = (locale: string): string => {
  switch (locale) {
    case "en":
      return "com";
    case "en-gb":
      return "co.uk";
    case "nl-be":
      return "be";
    case "it-it":
      return "it";
    case "es-es":
      return "es";
    case "pl-pl":
      return "pl";
    case "fr":
      return "fr";
    case "de":
      return "de";
    default:
      return "nl";
  }
};

export const checkGenderParamForLocale = (
  genderParam: string,
  locale: string
): boolean => {
  // Define gender parameter mappings for different locales
  const genderMappings: Record<string, string[]> = {
    nl: ["dames", "heren", "kinderen"],
    en: ["women", "men", "kids"],
    de: ["damen", "herren", "kinder"],
    fr: ["femme", "homme", "enfant"],
    "it-it": ["donna", "uomo", "bambini"],
    "es-es": ["mujer", "hombre", "ninos"],
    "pl-pl": ["kobiety", "mezczyzni", "dzieci"],
    "en-gb": ["women", "men", "kids"],
  };

  // Extract base locale (e.g., 'nl' from 'nl-nl') - only for it-it, pl-pl, es-es, and nl-be
  const shouldExtractBaseLocale = ["it-it", "pl-pl", "es-es", "nl-be"].includes(
    locale
  );
  let baseLocale = locale;
  if (shouldExtractBaseLocale) {
    baseLocale = locale.split("-")[0];
  }

  // Get valid gender params for the locale
  const validGenderParams =
    genderMappings[locale] ||
    genderMappings[baseLocale] ||
    // eslint-disable-next-line @typescript-eslint/dot-notation
    genderMappings["nl"];

  // Check if the gender parameter is valid for this locale
  return validGenderParams.includes(genderParam.toLowerCase());
};

// NOTE: Check Next Auth documentation on how to extend session object type
/* eslint-disable @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

const authRedirect = (ctx: NextPageContext, session: any): void => {
  if (!session.user.opt_in && !session.user.first_order) {
    (ctx?.res as any)?.cookie("client_opt_in", "1");
  }

  if (ctx.query.signin === "true") {
    return;
  }

  const resolveGender = () => {
    const shopPreference = session?.user?.shop_preference;

    if (
      ctx?.req?.intl?.locale === "en-us" &&
      shopPreference &&
      shopPreference.length &&
      shopPreference[0] === "kids"
    ) {
      return "women";
    }
    if (!shopPreference || !shopPreference.length) {
      return "women";
    } else {
      return shopPreference[0];
    }
  };

  const gender = resolveGender();
  let location = getI18nRoute({
    route: `/${gender}`,
    locale: ctx?.req?.intl?.locale || "en",
  });

  const urls = {
    shop_preference: "/onboarding?stage=shop_preference",
    brand_preference: "/onboarding?stage=brand_preference",
  };

  // FIXME: Object.keys() doesn't guarantee to follow the order of object keys
  // Source: https://stackoverflow.com/a/5525820
  const stages = Object.keys(urls);
  const nextStage =
    stages[
      stages.indexOf(session.user.onboarding_metadata.stage as string) + 1
    ] || stages[0];

  /* Redirect the user to onboarding page when the
   * user hasn't complete or partially complete onboarding flow
   * */

  if (
    session.user.onboarding_metadata.show_onboarding &&
    session.user.onboarding_metadata?.status === "incomplete"
  ) {
    location = urls[nextStage as keyof typeof urls];
  }

  ctx.res?.writeHead(302, {
    location,
  });
  ctx.res?.end();
};

const getUrlParams = (locale: string) => ({
  production: {
    domainWithoutLocale: "https://www.otrium.",
    ios: {
      ibi: "com.otrium.shop",
    },
    android: {
      apn: "com.otrium.shop",
    },
  },
  staging: {
    domainWithoutLocale: "https://staging.otrium.",
    ios: {
      ibi: "com.otrium.shop.staging",
    },
    android: {
      apn: "com.otrium.shop.staging",
    },
  },
});

const getDomainWithoutLocale = (locale: string) => {
  const isProd = process.env.RELEASE_STAGE === "production";

  return isProd
    ? getUrlParams(locale).production.domainWithoutLocale
    : getUrlParams(locale).staging.domainWithoutLocale;
};

const getGenderTranslation = (
  input: string,
  locale: string
): string | undefined => {
  const genderParam = input.split("?");
  const category = GENDER_CATEGORIES[genderParam[0].toLowerCase()];
  const translation = category
    ? GENDER_TRANSLATIONS[category][locale]
    : undefined;
  if (genderParam[1] && translation) {
    return `${translation}?${genderParam[1]}`;
  }
  return translation;
};

const getGenderTranslationForLocale = (
  genderTerm: string,
  targetLocale: string,
  fallbackLocale = "en"
): string => {
  return (
    getGenderTranslation(genderTerm, targetLocale) ||
    getGenderTranslation(genderTerm, fallbackLocale) ||
    genderTerm
  );
};

export const getRedirectionUrl = (
  pathInfo: PathInfo,
  locale: string
): string => {
  /**
   * pathInfo: {
   *   pathParams: [
   *     "marki",
   *     "river-woods",
   *     "dames",
   *   ],
   *   isGenderHomePage: false,
   *   isBrandPage: true,
   *   genderParam: "dames",
   *   brandParam: "river-woods",
   * }
   */

  // pathParams should not be empty and pathParams[0] should be a valid brand slug
  // valid brand slug structure: otrium.com/brand/brandSlug/genderSlug
  const { pathParams, isBrandPage } = pathInfo;
  if (!pathParams.length || !pathParams[0] || pathParams?.length < 3) {
    return "";
  }

  if (isBrandPage && pathInfo.genderParam) {
    // Check if genderParam belongs to current locale/domain
    const genderParamBelongsToCurrentDomain = checkGenderParamForLocale(
      pathInfo.genderParam,
      locale
    );
    const brandGeo = getBrandGeo(locale);
    if (
      !genderParamBelongsToCurrentDomain ||
      pathInfo?.pathParams[0] !== brandGeo
    ) {
      const domain = localeToDomain(locale);
      // Handle case where gender param doesn't belong to current domain
      const redirectionLink = buildHrefLink(pathInfo, [locale, domain]);
      // eslint-disable-next-line no-console
      console.log(
        `Redirecting user to ${redirectionLink} link, due to gender param mismatch`
      );
      return redirectionLink;
    }
  }

  return "";
};

const getBrandGeo = (locale: string): string => {
  return BRAND_GEO_MAP[locale] || "brands";
};

export const buildHrefLink = (
  pathInfo: PathInfo,
  localeDomain: [string, string]
): string => {
  const [locale, domain] = localeDomain;
  const baseDomain = `${getDomainWithoutLocale(locale)}${domain}`;

  if (pathInfo.isGenderHomePage && pathInfo.genderParam) {
    const genderForLocale = getGenderTranslationForLocale(
      pathInfo.genderParam,
      domain
    );
    return `${baseDomain}/${genderForLocale}`;
  }

  if (pathInfo.isBrandPage && pathInfo.genderParam && pathInfo.brandParam) {
    const genderForLocale = getGenderTranslationForLocale(
      pathInfo.genderParam,
      domain
    );
    const brandGeo = getBrandGeo(locale);
    return `${baseDomain}/${brandGeo}/${pathInfo.brandParam}/${genderForLocale}`;
  }

  // Default case - return original path
  return `${baseDomain}/${pathInfo.pathParams.join("/")}`;
};

const getMetaTags = (ctx?: NextPageContext) => {
  try {
    // Note: issue with the redirection logic, need to fix the pathInfo parsing
    const pathInfo = parsePathInfo(ctx?.asPath || "");
    const locale = ctx?.req?.intl?.locale || "nl";
    const redirectionUrl = getRedirectionUrl(pathInfo, locale);
    const isClient = typeof window !== "undefined";

    if (redirectionUrl) {
      // Check if current URL matches redirection URL to prevent infinite loops
      const currentUrl = `${
        ctx?.req?.headers?.host
          ? `https://${ctx?.req?.headers.host || ""}`
          : isClient
          ? window.location?.origin
          : ""
      }${ctx?.asPath || ""}`;
      if (currentUrl !== redirectionUrl) {
        // redirect to the correct URL
        ctx?.res?.writeHead(301, {
          Location: redirectionUrl,
        });
        ctx?.res?.end();
        return null; // Return null to avoid rendering meta tags after redirection
      }
    }

    // URL construction and cleaning
    const origin = ctx?.req?.headers?.host
      ? `https://${ctx.req.headers.host || ""}`
      : isClient
      ? window.location?.origin
      : "";
    const pathname = ctx?.req?.url || "/";

    if (!pathname || !origin) {
      return null;
    }

    const url = new URL(pathname, origin);
    url.pathname = url.pathname.replace(/\.json$/, "");

    const cleanedPathname = url.pathname?.replace(
      /^\/_next\/data\/[^/]+\//,
      "/"
    );
    const pathnameWithoutParams = cleanedPathname.split("?")[0];
    const canonical = (
      <link rel="canonical" href={`${origin}${pathnameWithoutParams}`} />
    );

    return <>{canonical}</>;
  } catch (error) {
    console.error("Error generating meta tags:", error);
    // Return minimal fallback
    return (
      <link
        rel="canonical"
        href={
          ctx?.req ? `https://${ctx.req.headers.host}` : window.location.origin
        }
      />
    );
  }
};

const getQueryString = (query: ParsedUrlQuery) => {
  return Object.keys(query)
    .map((key) => {
      const value = query[key];
      if (Array.isArray(value)) {
        return value
          .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
          .join("&");
      }
      return value
        ? `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        : "";
    })
    .filter(Boolean)
    .join("&");
};

export { authRedirect, getUrlParams, getMetaTags, getQueryString };
